/* omes-track.js — what a visitor does on omes.tn, told to the marketplace and to PostHog
   (omes-storefront backlog TK-12; contract omes_multi_vendor/app/constants/visitor-events.ts).
   Vanilla JS, no dependencies, loaded with defer by snippets/omes-track.liquid, which also writes
   the page's facts in #omes-track-config (page type and handle, the product, the collection, the
   signed-in customer, the PostHog key, the collector's address).

   Rules this file keeps, in order:
   1. Consent first. Nothing is read, written or sent before Shopify's Customer Privacy API says
      analytics processing is allowed; where no banner is required (Tunisia) that is at once,
      where one is, it is after the visitor's yes (`visitorConsentCollected`). A no means no cookie.
   2. One vocabulary. Every event is a name of the contract with the keys it lists; the collector
      refuses anything else and the mirror below must be edited with the registry.
   3. One visitor id. `omes_vid` (13 months) is the collector's visitorId and PostHog's distinct id,
      so a PostHog cohort joins to the marketplace's tables. `omes_sid` ends after 30 minutes idle.
   4. Light. Batches of at most 50 events, flushed every 5 s and on pagehide, sent as text/plain by
      sendBeacon (no preflight). Impressions are observed, never polled.
   Also here: PostHog's feature flags, told to the collector as `context.variants` and read by the page
   through `omesTrack.variant(key)` (TK-23), and the « Vu récemment » rail drawn from localStorage (TK-30).
   5. The order tunnel is a page of the theme (/pages/livraison, sections/omes-checkout.liquid, since 20 Oct
      2026): it calls omesTrack with checkout_started / checkout_step / checkout_alert / order_completed. Every
      batch says `context.emitter = "theme"`, so the collector does not forward those four to PostHog a second
      time. Not here: Shopify's own checkout, seen by the app's web pixel (TK-13).
   6. Robots and OMES's own traffic count for nothing (22 Oct 2026). Nothing runs when the browser says it is
      automated (navigator.webdriver, a HeadlessChrome brand, the globals Selenium, Puppeteer, PhantomJS,
      Nightmare and Cypress leave behind) or when the window is a size no one shops from (page-capture robots:
      1050 × 1893, 412 × 1893, 1920 × 10 000). `?omes_internal=1` sets a one-year cookie that silences the
      tracker on a team device (`?omes_internal=0` removes it); `?omes_internal=test` lets a check script run it:
      PostHog is not loaded and every batch carries `context.internal = true`, which every reading skips. */
(() => {
  if (window.omesTrack) return;
  const cfgEl = document.getElementById('omes-track-config');
  if (!cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || cfg.disabled || navigator.webdriver) return;

  /* ─── the contract's mirror: names and the keys the theme sends (TK-10) ─── */
  const V = 1;
  const MAX_BATCH = 50;
  const FLUSH_MS = 5000;
  const SESSION_MIN = 30;
  const VID_DAYS = 395;
  const EVENTS = {
    page_viewed: 1, section_viewed: 1, nav_clicked: 1, banner_clicked: 1, chip_clicked: 1, search_opened: 1,
    product_impression: 1, product_clicked: 1, collection_viewed: 1, filter_applied: 1, sort_changed: 1, empty_result: 1, load_more: 1, quick_view_opened: 1,
    search_submitted: 1, zero_results: 1, search_result_clicked: 1,
    product_viewed: 1, variant_selected: 1, size_unavailable: 1, size_guide_opened: 1, accordion_opened: 1, image_swiped: 1,
    add_to_cart: 1, add_to_cart_failed: 1, remove_from_cart: 1, wishlist_added: 1, wishlist_removed: 1,
    cart_viewed: 1, promo_code_entered: 1, promo_code_failed: 1, checkout_clicked: 1, checkout_started: 1, checkout_step: 1, checkout_alert: 1, order_completed: 1,
    look_viewed: 1, look_added: 1, brand_page_viewed: 1, brand_followed: 1, brand_unfollowed: 1, event_viewed: 1, notify_me_clicked: 1,
    help_viewed: 1, faq_opened: 1, contact_submitted: 1, login_attempted: 1, account_created: 1, wishlist_viewed: 1,
    feed_shown: 1, promo_shown: 1, promo_clicked: 1, nudge_shown: 1, nudge_clicked: 1, survey_answered: 1, error_shown: 1,
  };

  // The events that always get a replay (see captureInPosthog).
  const RECORD_ON = { checkout_started: 1, checkout_alert: 1, order_completed: 1, add_to_cart_failed: 1, error_shown: 1 };

  /* ─── small helpers ─── */
  const now = () => new Date().toISOString();
  const rand = (n) => { const a = new Uint8Array(n); (window.crypto || window.msCrypto).getRandomValues(a); return Array.from(a, (b) => (b % 36).toString(36)).join(''); };
  const uuid = () => (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); });
  const getCookie = (name) => { const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : null; };
  const setCookie = (name, value, seconds) => { document.cookie = name + '=' + encodeURIComponent(value) + '; Max-Age=' + seconds + '; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : ''); };
  const device = () => (matchMedia('(max-width: 767px)').matches ? 'mobile' : matchMedia('(max-width: 1023px)').matches ? 'tablet' : 'desktop');
  const num = (v) => { const n = typeof v === 'string' ? parseFloat(v) : v; return typeof n === 'number' && isFinite(n) ? n : undefined; };
  const clean = (o) => { const out = {}; Object.keys(o).forEach((k) => { if (o[k] !== undefined && o[k] !== null && o[k] !== '') out[k] = o[k]; }); return out; };
  const utm = () => { const p = new URLSearchParams(location.search); const u = {}; ['source', 'medium', 'campaign', 'content', 'term'].forEach((k) => { const v = p.get('utm_' + k); if (v) u[k] = v.slice(0, 200); }); return Object.keys(u).length ? u : undefined; };
  const store = { get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } }, set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } } };
  const local = { get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } }, set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } } };

  /* ─── robots, and OMES's own traffic ─── */
  // The viewports no one shops from: the collector applies the same rule (impossibleViewport), this saves the request.
  const impossibleViewport = (w, h) => w < 200 || h < 200 || h > 2200 || (h > 1700 && w < 1200);
  const automated = () => {
    try {
      if (navigator.webdriver) return true;
      const brands = navigator.userAgentData && navigator.userAgentData.brands;
      if (brands && brands.some((b) => /headless/i.test(b.brand || ''))) return true;
      if (/HeadlessChrome|PhantomJS|Lighthouse|Electron/i.test(navigator.userAgent)) return true;
      if (window._phantom || window.callPhantom || window.__nightmare || window.Cypress || window.domAutomation || window.domAutomationController) return true;
      if (document.__selenium_unwrapped || document.__webdriver_evaluate || document.__driver_evaluate) return true;
      if (impossibleViewport(window.innerWidth, window.innerHeight)) return true;
    } catch (e) { /* a browser that refuses to answer is a browser */ }
    return false;
  };
  if (automated()) return;
  const internalParam = new URLSearchParams(location.search).get('omes_internal');
  if (internalParam === '1' || internalParam === 'test') setCookie('omes_internal', internalParam, 365 * 86400);
  else if (internalParam === '0') setCookie('omes_internal', '', 0);
  const internalMode = getCookie('omes_internal');
  if (internalMode === '1') return; // a team device: nothing is sent, nothing is set
  const INTERNAL_TEST = internalMode === 'test'; // a check script: the collector hears it, flagged; PostHog does not

  /* ─── identity: the visitor and the session ─── */
  let visitorId = null;
  let sessionId = null;
  let returning = false;
  function openIdentity() {
    visitorId = getCookie('omes_vid');
    returning = !!visitorId || local.get('omes_seen') === '1';
    if (!visitorId) visitorId = 'v_' + rand(32);
    setCookie('omes_vid', visitorId, VID_DAYS * 86400);
    local.set('omes_seen', '1');
    touchSession();
  }
  function touchSession() {
    sessionId = getCookie('omes_sid') || ('s_' + Date.now().toString(36) + '_' + rand(6));
    setCookie('omes_sid', sessionId, SESSION_MIN * 60);
  }

  /* ─── the queue and the collector ─── */
  const queue = [];
  let timer = null;
  let consent = { analytics: false, personalisation: false };
  let posthogReady = false;
  // What was tracked before posthog-js finished loading (product_viewed, collection_viewed… fire at start): told to it once loaded.
  const pendingPosthog = [];
  const seenIds = new Set();

  function envelope(events) {
    return {
      v: V, site: 'shopify', visitorId, sessionId, customerId: cfg.customerId || null, consent,
      context: clean({ url: location.href.slice(0, 2048), referrer: document.referrer ? document.referrer.slice(0, 2048) : null, utm: utm(), device: device(), viewport: { w: window.innerWidth, h: window.innerHeight }, language: (document.documentElement.lang || navigator.language || 'fr').slice(0, 16), returning, variants: Object.keys(flagVariants).length ? flagVariants : undefined, emitter: 'theme', internal: INTERNAL_TEST || undefined }),
      events,
    };
  }
  function flush() {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!queue.length || !cfg.collector) return;
    const events = queue.splice(0, MAX_BATCH);
    const body = JSON.stringify(envelope(events));
    let sent = false;
    try { sent = !!(navigator.sendBeacon && navigator.sendBeacon(cfg.collector, body)); } catch (e) { sent = false; }
    if (!sent) {
      fetch(cfg.collector, { method: 'POST', body, keepalive: true, mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'text/plain;charset=UTF-8' } }).catch(() => {});
    }
    if (queue.length) flush();
  }
  function track(name, props, page) {
    if (!EVENTS[name] || !consent.analytics) return;
    const id = uuid();
    if (seenIds.has(id)) return;
    seenIds.add(id);
    touchSession();
    const cleaned = clean(props || {});
    queue.push({ eventId: id, name, at: now(), page: page || cfg.page || { type: 'other' }, props: cleaned });
    const phProps = Object.assign({ page_type: (page || cfg.page || {}).type }, cleaned);
    if (posthogReady && window.posthog) captureInPosthog(name, phProps);
    else if (pendingPosthog.length < MAX_BATCH) pendingPosthog.push([name, phProps]);
    if (queue.length >= MAX_BATCH) flush();
    else if (!timer) timer = setTimeout(flush, FLUSH_MS);
    if (cfg.debug) console.debug('[omes-track]', name, cleaned);
  }
  function captureInPosthog(name, props) {
    try { window.posthog.capture(name, props); } catch (e) { /* never in the way */ }
    // A session that reaches the order, or hits a wall, is always replayed: PostHog's sampling is overridden for it.
    if (RECORD_ON[name]) { try { if (window.posthog.startSessionRecording) window.posthog.startSessionRecording({ sampling: true }); } catch (e) { /* noop */ } }
  }
  /* ─── feature flags (TK-23): PostHog's, read once loaded, told to the collector with every batch ─── */
  const KNOWN_FLAGS = ['omes-recent-rail'];
  const flagVariants = {};
  let flagsResolved = false;
  const flagWaiters = [];
  const resolveFlags = () => { if (flagsResolved) return; flagsResolved = true; flagWaiters.splice(0).forEach((fn) => { try { fn(); } catch (e) { /* noop */ } }); document.dispatchEvent(new CustomEvent('omes:flags', { detail: Object.assign({}, flagVariants) })); };
  const whenFlags = (fn) => { if (flagsResolved) fn(); else flagWaiters.push(fn); };
  window.omesTrack = track;
  window.omesTrack.variant = (key) => flagVariants[key];
  window.omesTrack.whenFlags = whenFlags;
  addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });

  /* ─── PostHog: autocapture, replay, flags — same distinct id as the collector ─── */
  function loadPosthog() {
    if (!cfg.posthogKey || window.posthog || INTERNAL_TEST) return;
    const host = cfg.posthogHost || 'https://eu.i.posthog.com';
    const s = document.createElement('script');
    s.async = true; s.crossOrigin = 'anonymous';
    s.src = host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
    s.onload = () => {
      if (!window.posthog || !window.posthog.init) return;
      window.posthog.init(cfg.posthogKey, {
        api_host: host, ui_host: host.replace('.i.posthog.com', '.posthog.com'),
        persistence: 'localStorage+cookie', person_profiles: 'identified_only',
        capture_pageview: false, capture_pageleave: true, autocapture: true, rageclick: true,
        session_recording: { maskAllInputs: true, maskTextSelector: '[data-omes-mask]' },
        bootstrap: { distinctID: visitorId, isIdentifiedID: false },
        loaded: (ph) => {
          posthogReady = true;
          try { ph.register({ site: 'shopify', page_type: cfg.page && cfg.page.type, device: device(), omes_vid: visitorId, returning, signed_in: !!cfg.customerId }); } catch (e) { /* noop */ }
          // A signed-in customer is one person across their devices: PostHog merges this browser's omes_vid into them.
          if (cfg.customerId) { try { ph.identify(cfg.customerId); } catch (e) { /* noop */ } }
          try { ph.capture('$pageview', { page_type: cfg.page && cfg.page.type, page_handle: cfg.page && cfg.page.handle }); } catch (e) { /* noop */ }
          pendingPosthog.splice(0).forEach(([name, props]) => captureInPosthog(name, props));
          try { ph.onFeatureFlags(() => { KNOWN_FLAGS.forEach((key) => { const v = ph.getFeatureFlag(key); if (v !== undefined && v !== null && v !== false) flagVariants[key] = String(v); }); resolveFlags(); }); } catch (e) { resolveFlags(); }
        },
      });
    };
    document.head.appendChild(s);
  }

  /* ─── product cards: which list, which position, which product ─── */
  const CARD = '.hp-card, .omes-prc, .omes-pcard, .pcard, [data-omes-card], product-card, .product-card, .grid-item, .omes-event-tile';
  const pageType = () => (cfg.page && cfg.page.type) || 'other';
  // A Shopify section's DOM id is `shopify-section-template--<n>__<key>` (the hp-* sections write `hp-template--<n>__<key>`);
  // the key is what index.json calls it: omes-nouveautes, omes-looks.
  const sectionKey = (el) => { const id = el && el.id; return id ? id.replace(/^(shopify-section-|hp-)/, '').replace(/^template--\d+__/, '').slice(0, 80) : undefined; };
  const sectionKind = (key, el) => {
    const k = key || '';
    if (/hero|slideshow/.test(k)) return 'hero';
    if (/look/.test(k)) return 'look';
    if (/marques|brands/.test(k)) return 'brands';
    if (/categories|essentiels|circles/.test(k)) return 'circles';
    if (/services/.test(k)) return 'services';
    if (/oclub|band|banner|editorial/.test(k)) return 'banner';
    if (/footer/.test(k)) return 'footer';
    if (/header|menu/.test(k)) return 'header';
    if (el && el.querySelector(CARD)) return 'rail';
    return (el && (el.className || '').match(/\b(hp|stl|hero|rail|band|brands|services|footer|header)\b/) || [])[1];
  };
  function listOf(card) {
    const explicit = card.closest('[data-omes-list]');
    if (explicit) { try { return JSON.parse(explicit.getAttribute('data-omes-list')); } catch (e) { return { kind: 'rail', railId: explicit.getAttribute('data-omes-list') }; } }
    const hp = card.closest('section.hp');
    if (hp) return { kind: 'rail', railId: hp.id };
    const t = pageType();
    if (t === 'collection') return clean({ kind: cfg.collection && cfg.collection.kind || 'collection', handle: cfg.page.handle, page: cfg.collection && cfg.collection.page, sort: cfg.collection && cfg.collection.sort, filters: cfg.collection && cfg.collection.filters });
    if (t === 'search') return { kind: 'search', handle: cfg.search && cfg.search.query };
    if (t === 'product') return clean({ kind: 'related', railId: sectionKey(card.closest('.shopify-section')) });
    if (t === 'cart') return { kind: 'cart' };
    if (t === 'look') return { kind: 'look', handle: cfg.page.handle };
    if (t === 'brand') return clean({ kind: 'brand', handle: cfg.page.handle, railId: sectionKey(card.closest('.shopify-section')) });
    return clean({ kind: 'rail', railId: sectionKey(card.closest('.shopify-section')) || sectionKey(card.closest('section')) });
  }
  function productOf(card) {
    const id = card.getAttribute('data-id') || card.getAttribute('data-product-id') || (card.querySelector('[data-id]') || {}).getAttribute && (card.querySelector('[data-id]') || { getAttribute: () => null }).getAttribute('data-id');
    const a = card.matches('a[href]') ? card : card.querySelector('a[href*="/products/"]');
    const handle = a ? (a.getAttribute('href').match(/\/products\/([^/?#]+)/) || [])[1] : undefined;
    return clean({ shopifyProductId: id ? String(id) : undefined, brand: card.getAttribute('data-v') || card.getAttribute('data-vendor') || undefined, audience: card.getAttribute('data-a') || undefined, handle });
  }
  function positionOf(card) {
    // The list is the nearest ancestor holding more than one card (a grid wraps each card in its own cell).
    let box = card.parentElement;
    for (let i = 0; i < 6 && box && box !== document.body; i += 1) { if (box.querySelectorAll(CARD).length > 1) break; box = box.parentElement; }
    if (!box) return 1;
    const cards = Array.from(box.querySelectorAll(CARD)).filter((el) => !el.classList.contains('hp-x'));
    const i = cards.indexOf(card);
    return i < 0 ? 1 : i + 1;
  }
  const impressed = new WeakSet();
  const dwell = new WeakMap();
  function observeCards(root) {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const card = e.target;
        if (e.isIntersecting && e.intersectionRatio >= 0.5) {
          if (impressed.has(card) || dwell.has(card)) return;
          dwell.set(card, setTimeout(() => {
            dwell.delete(card);
            if (!card.isConnected || card.classList.contains('hp-x')) return;
            const p = productOf(card);
            if (!p.shopifyProductId) return;
            impressed.add(card);
            const { handle, ...rest } = p;
            track('product_impression', Object.assign({ list: listOf(card), position: positionOf(card) }, rest));
          }, 500));
        } else if (dwell.has(card)) { clearTimeout(dwell.get(card)); dwell.delete(card); }
      });
    }, { threshold: [0.5] });
    const watch = (node) => node.querySelectorAll(CARD).forEach((card) => { if (!impressed.has(card)) io.observe(card); });
    watch(root);
    new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) { if (n.matches(CARD)) io.observe(n); watch(n); } }))).observe(root, { childList: true, subtree: true });
  }
  document.addEventListener('click', (ev) => {
    const card = ev.target.closest && ev.target.closest(CARD);
    if (!card || !card.querySelector('a[href*="/products/"]') && !card.matches('a[href*="/products/"]')) return;
    if (ev.target.closest('.addwishlist, .omes-plp-heart, [data-fav], .fav')) return; // the heart is its own event
    const p = productOf(card);
    if (!p.shopifyProductId) return;
    const list = listOf(card);
    const { handle, ...rest } = p;
    store.set('omes_source', JSON.stringify(list));
    track('product_clicked', Object.assign({ list, position: positionOf(card) }, rest));
  }, true);

  /* ─── sections seen: scroll depth, one event per section ─── */
  function observeSections() {
    if (!('IntersectionObserver' in window)) return;
    const sections = Array.from(document.querySelectorAll('section.hp, .shopify-section'));
    const seen = new WeakSet();
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting || seen.has(e.target)) return;
      seen.add(e.target);
      const el = e.target;
      const inner = el.querySelector('[id^="shopify-section"], .hp-sec');
      const key = sectionKey(el) || sectionKey(inner) || 'section';
      track('section_viewed', clean({ sectionId: key, position: sections.indexOf(el) + 1, sectionKind: sectionKind(key, el) }));
    }), { threshold: [0.25] });
    sections.forEach((s) => io.observe(s));
  }

  /* ─── homepage, navigation, banners ─── */
  document.addEventListener('click', (ev) => {
    const t = ev.target;
    if (!t.closest || t.closest(CARD)) return; // a product card is its own event
    // Chips, and the homepage's category and essentials circles (omes-circles: a.ess, a.ess.cat).
    const chip = t.closest('[data-hp-chips] button, [data-hp-chips] a, .hp-chip, .omes-home a.ess');
    if (chip) {
      const sec = chip.closest('.shopify-section, section.hp');
      const group = chip.closest('[data-hp-chips]') ? chip.closest('[data-hp-chips]').getAttribute('data-hp-chips') || 'chips' : chip.classList.contains('cat') ? 'categories' : chip.classList.contains('ess') ? 'essentiels' : undefined;
      track('chip_clicked', clean({ chip: (chip.getAttribute('data-hp-chip') || (chip.querySelector('b') || chip).textContent || '').trim().slice(0, 60), group, sectionId: sectionKey(sec) || (sec || {}).id }));
      return;
    }
    const nav = t.closest('header a[href], .mega-menu a[href], nav a[href], .site-nav a[href]');
    if (nav) { track('nav_clicked', clean({ target: nav.getAttribute('href').slice(0, 200), label: (nav.textContent || '').trim().slice(0, 60) })); return; }
    // Everything else that leads somewhere from a homepage section: hero CTAs (.lbSlide), the O'Club button, « Voir tout »,
    // the brand feature and brand cards (omes-brands: a.bs, a.bcard, told as brand:<handle>), look cards (look:<handle>).
    const banner = t.closest('.hp-hero a[href], .hp-band a[href], .hp-oclub a[href], .slideshow a[href], [class*="banner"] a[href], .stl-banner a[href], .omes-home a[href], a.hp-lc, .stl-banner-rail a.cd, .stl-looks-grid a[href]');
    if (banner) {
      const sec = banner.closest('.shopify-section, section.hp');
      const href = banner.getAttribute('href') || '';
      const look = (href.match(/\/pages\/looks\/([^/?#]+)/) || [])[1];
      // A brand card leads to /collections/<handle>, /pages/<handle> or /collections/vendors?q=<name>.
      const brand = banner.classList.contains('bcard') || banner.classList.contains('bs') ? ((href.match(/\/collections\/vendors\?q=([^&#]+)/) || [])[1] || (href.match(/\/(?:collections|pages)\/([^/?#]+)/) || [])[1]) : undefined;
      const slide = banner.closest('.lbSlide');
      const title = slide ? ((slide.querySelector('h1, h2, strong') || {}).textContent || '') : '';
      const label = look ? 'look:' + look : brand ? 'brand:' + decodeURIComponent(brand.replace(/\+/g, ' ')) : (title || banner.getAttribute('aria-label') || banner.textContent || '');
      track('banner_clicked', clean({ target: href.slice(0, 200), slot: sectionKey(sec) || (sec || {}).id, bannerId: String(label).replace(/\s+/g, ' ').trim().slice(0, 60) }));
    }
  }, true);

  /* ─── the product page ─── */
  function productPage() {
    const p = cfg.product;
    if (!p) return;
    let source;
    try { source = JSON.parse(store.get('omes_source') || 'null') || undefined; } catch (e) { source = undefined; }
    const base = () => clean({ shopifyProductId: p.shopifyProductId, shopifyVariantId: currentVariant(), brand: p.brand, family: p.family, audience: p.audience, price: num(p.price) });
    const root = document.querySelector('.omes-pdp, [data-omes-pdp], .product-single, main');
    const currentVariant = () => { const i = document.querySelector('[data-omes-variant-id], form[action*="/cart/add"] [name="id"]'); return i && i.value ? String(i.value) : undefined; };
    track('product_viewed', Object.assign(base(), { source }));
    rememberProduct(p);
    if (!root) return;
    // What is already selected when the page opens is the theme's default, not the shopper's choice.
document.querySelectorAll('.omes-size--active, .omes-swatch--active').forEach((b) => b.setAttribute('data-omes-tracked', b.getAttribute('data-omes-option-label') || ''));
    document.addEventListener('omes:variant-change', () => {
      document.querySelectorAll('.omes-size--active, .omes-swatch--active').forEach((b) => {
        const group = b.closest('[data-omes-option-group]');
        const label = group && group.querySelector('.omes-variant__label') ? group.querySelector('.omes-variant__label').textContent.replace(/—.*$/, '').trim() : (b.classList.contains('omes-size') ? 'Taille' : 'Couleur');
        if (b.getAttribute('data-omes-tracked') === b.getAttribute('data-omes-option-label')) return;
        b.setAttribute('data-omes-tracked', b.getAttribute('data-omes-option-label') || '');
        track('variant_selected', Object.assign(base(), { option: label.slice(0, 40), value: (b.getAttribute('data-omes-option-label') || b.textContent || '').trim().slice(0, 40), available: !b.classList.contains('omes-size--disabled') }));
      });
    }, true);
    document.addEventListener('click', (ev) => {
      const pill = ev.target.closest && ev.target.closest('.omes-size--disabled, .omes-size[aria-disabled="true"]');
      if (pill) track('size_unavailable', Object.assign(base(), { value: (pill.getAttribute('data-omes-option-label') || pill.textContent || '').trim().slice(0, 40) }));
    }, true);
    root.querySelectorAll('details').forEach((d) => d.addEventListener('toggle', () => {
      if (!d.open) return;
      const title = (d.querySelector('summary') || {}).textContent || '';
      const section = title.trim().slice(0, 60) || 'section';
      if (/guide des tailles|guide de taille|size guide|pointure/i.test(section) || d.querySelector('.omes-sizeguide')) track('size_guide_opened', base());
      track('accordion_opened', Object.assign(base(), { section }));
    }));
    const thumbs = document.querySelector('[data-omes-thumbs]');
    if (thumbs) {
      let last = -1;
      new MutationObserver(() => {
        const all = Array.from(thumbs.querySelectorAll('.omes-gallery-thumb'));
        const i = all.findIndex((t) => t.classList.contains('omes-gallery-thumb--active'));
        if (i >= 0 && i !== last) { if (last >= 0) track('image_swiped', Object.assign(base(), { index: i + 1, count: all.length })); last = i; }
      }).observe(thumbs, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }
    document.addEventListener('submit', (ev) => {
      const form = ev.target;
      if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      const id = form.querySelector('[name="id"]');
      if (!id || !id.value) track('add_to_cart_failed', Object.assign(base(), { reason: 'no_variant' }));
    }, true);
  }

  /* ─── the cart: adds, removes, the drawer, the checkout button ─── */
  function cartHooks() {
    const onAdd = (payload, ok, status, text) => {
      const items = payload && Array.isArray(payload.items) ? payload.items : [payload || {}];
      if (!ok) { track('add_to_cart_failed', clean({ shopifyVariantId: items[0] && items[0].id ? String(items[0].id) : undefined, shopifyProductId: cfg.product && cfg.product.shopifyProductId, reason: status === 422 ? 'out_of_stock' : 'error_' + status })); if (status !== 422) track('error_shown', { kind: 'cart_add', status }); return; }
      let source; try { source = JSON.parse(store.get('omes_source') || 'null') || undefined; } catch (e) { source = undefined; }
      items.forEach((it) => track('add_to_cart', clean({ shopifyVariantId: it.id ? String(it.id) : undefined, shopifyProductId: cfg.product && cfg.product.shopifyProductId, quantity: num(it.quantity) || 1, price: cfg.product ? num(cfg.product.price) : undefined, source })));
      if (pageType() === 'look' && items.length > 1) track('look_added', { lookHandle: cfg.page.handle, piecesKept: items.length });
      if (text) { try { const j = JSON.parse(text); const line = j.items ? j.items[0] : j; if (line && line.product_id && items.length === 1) { /* nothing more needed */ } } catch (e) { /* ignore */ } }
    };
    const onChange = (payload, text) => {
      const before = lastCart;
      rememberCart(text);
      if (before && lastCart) {
        const after = {}; lastCart.items.forEach((it) => { after[it.key] = it; });
        before.items.forEach((it) => { if (!after[it.key]) track('remove_from_cart', clean({ shopifyVariantId: String(it.variant_id), shopifyProductId: it.product_id ? String(it.product_id) : undefined, quantity: it.quantity || 1, price: it.price ? it.price / 100 : undefined })); });
        return;
      }
      if (payload && num(payload.quantity) === 0 && payload.id) track('remove_from_cart', { shopifyVariantId: String(payload.id), quantity: 1 });
    };
    const parseBody = (body) => {
      if (!body) return null;
      try {
        if (typeof body === 'string') { try { return JSON.parse(body); } catch (e) { const p = new URLSearchParams(body); const o = {}; p.forEach((v, k) => { o[k] = v; }); return o; } }
        if (body instanceof FormData || body instanceof URLSearchParams) { const o = {}; body.forEach((v, k) => { o[k] = v; }); return o; }
      } catch (e) { /* ignore */ }
      return null;
    };
    const route = (url) => { const u = String(url || ''); if (/\/cart\/add(\.js)?(\?|$)/.test(u)) return 'add'; if (/\/cart\/(change|update)(\.js)?(\?|$)/.test(u)) return 'change'; if (/\/cart\.js(\?|$)/.test(u)) return 'cart'; return null; };
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input && input.url;
      const r = route(url);
      const p = origFetch.apply(this, arguments);
      if (r) p.then((res) => { const payload = parseBody(init && init.body); if (r === 'add') res.clone().text().then((t) => onAdd(payload, res.ok, res.status, t), () => onAdd(payload, res.ok, res.status)); else if (r === 'cart') res.clone().text().then(rememberCart, () => {}); else res.clone().text().then((t) => onChange(payload, t), () => onChange(payload)); }).catch(() => {});
      return p;
    };
    const open = XMLHttpRequest.prototype.open; const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, url) { this.__omesRoute = route(url); return open.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function (body) {
      if (this.__omesRoute) { const r = this.__omesRoute; const payload = parseBody(body); this.addEventListener('loadend', () => { if (r === 'add') onAdd(payload, this.status >= 200 && this.status < 300, this.status, this.responseText); else if (r === 'cart') rememberCart(this.responseText); else onChange(payload, this.responseText); }); }
      return send.apply(this, arguments);
    };
    const drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (drawer) new MutationObserver(() => { if (drawer.classList.contains('active') && !drawer.__omesOpen) { drawer.__omesOpen = true; cartViewed('drawer'); } else if (!drawer.classList.contains('active')) drawer.__omesOpen = false; }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('click', (ev) => {
      const b = ev.target.closest && ev.target.closest('[name="checkout"], a[href="/checkout"], a[href^="/checkout?"], .cart__checkout, .checkout-btn');
      if (b) cartSummary().then((c) => track('checkout_clicked', { lines: c.lines, total: c.total }));
    }, true);
  }
  // The cart as last seen (/cart.js, /cart/change.js answers): what a change removed is the difference.
  let lastCart = null;
  function rememberCart(text) { try { const j = JSON.parse(text); if (j && Array.isArray(j.items)) lastCart = j; } catch (e) { /* not a cart */ } }
  function cartSummary() {
    return origCartFetch('/cart.js').then((r) => r.text()).then((t) => { rememberCart(t); const c = JSON.parse(t); return { lines: c.item_count, total: Math.round(c.total_price) / 100 }; }).catch(() => ({ lines: 0, total: 0 }));
  }
  const origCartFetch = window.fetch.bind(window);
  function cartViewed(surface) { cartSummary().then((c) => track('cart_viewed', clean({ lines: c.lines, total: c.total, surface }))); }

  /* ─── search ─── */
  function searchHooks() {
    document.addEventListener('focusin', (ev) => { if (ev.target.matches && ev.target.matches('input[type="search"], input[name="q"]') && !ev.target.__omesOpened) { ev.target.__omesOpened = true; track('search_opened', {}); } });
    let lastQ = '';
    const report = () => {
      clearTimeout(report.__t);
      const input = document.querySelector('input[name="q"]:focus') || document.querySelector('input[name="q"]');
      const q = input && input.value ? input.value.trim().slice(0, 120) : '';
      // theme.js asks Shopify from three characters; under that the panel says nothing about this query.
      if (!q || q === lastQ || q.length < 3) return;
      // #serch-list alone: the drawer keeps « Produits populaires » in a list with the same id beside it.
      const panel = document.getElementById('serch-list');
      const none = !!panel && !!panel.querySelector('.noResult');
      const items = panel ? panel.querySelectorAll('li, .search-suggest a').length : 0;
      if (!none && !items) return; // Shopify has not answered yet
      lastQ = q;
      track('search_submitted', { query: q, resultCount: items, suggestionUsed: false, engine: 'predictive' });
      if (none) track('zero_results', { query: q });
    };
    // The theme rebuilds the predictive panel from fetched HTML on every keystroke, with or without a
    // results list: any change while the search field has focus is a reason to look again. The wait is
    // long enough to report what the visitor settled on, not each letter of it.
    new MutationObserver(() => { const a = document.activeElement; if (!a || !a.matches || !a.matches('input[name="q"], input[type="search"]')) return; clearTimeout(report.__t); report.__t = setTimeout(report, 1500); }).observe(document.body, { childList: true, subtree: true });
    if (pageType() === 'search' && cfg.search) { track('search_submitted', { query: cfg.search.query, resultCount: num(cfg.search.resultCount) || 0, suggestionUsed: false, engine: 'page' }); if ((num(cfg.search.resultCount) || 0) === 0) track('zero_results', { query: cfg.search.query }); }
    document.addEventListener('click', (ev) => {
      const a = ev.target.closest && ev.target.closest('#serch-list a[href*="/products/"]');
      if (!a) return;
      report(); // a tap can beat the wait: the search goes first, or the click has no query
      const li = a.closest('li'); const all = li ? Array.from(li.parentElement.children) : [];
      track('search_result_clicked', clean({ query: lastQ || (document.querySelector('input[name="q"]') || {}).value, position: li ? all.indexOf(li) + 1 : 1, shopifyProductId: (a.closest('[data-id]') || li && li.querySelector('[data-id]') || { getAttribute: () => null }).getAttribute('data-id') || undefined }));
    }, true);
  }

  /* ─── collections: filters, sort, empty grids, pages ─── */
  function collectionHooks() {
    if (pageType() !== 'collection') return;
    const c = cfg.collection || {};
    const params = (u) => { const p = new URL(u, location.origin).searchParams; const f = {}; let sort; let page; p.forEach((v, k) => { if (k.indexOf('filter.') === 0 && v !== '') f[k.replace('filter.', '')] = v; else if (k === 'sort_by') sort = v; else if (k === 'page') page = v; }); return { f, sort, page }; };
    let prev = params(location.href);
    track('collection_viewed', clean({ handle: cfg.page.handle, kind: c.kind || 'collection', page: num(prev.page || c.page), sort: prev.sort || c.sort, filters: Object.keys(prev.f).length ? prev.f : undefined, resultCount: num(c.count) }));
    const onUrl = () => {
      const cur = params(location.href);
      Object.keys(cur.f).forEach((k) => { if (cur.f[k] !== prev.f[k] && cur.f[k] !== '') track('filter_applied', clean({ facet: k.slice(0, 60), value: String(cur.f[k]).slice(0, 80), handle: cfg.page.handle })); });
      if (cur.sort && cur.sort !== prev.sort) track('sort_changed', { sort: cur.sort, handle: cfg.page.handle });
      if (cur.page && cur.page !== prev.page) track('load_more', { handle: cfg.page.handle, page: num(cur.page) });
      prev = cur;
      clearTimeout(onUrl.__t); onUrl.__t = setTimeout(() => { if (!document.querySelector(CARD + ' a[href*="/products/"]')) track('empty_result', clean({ handle: cfg.page.handle, filters: Object.keys(cur.f).length ? cur.f : undefined, sort: cur.sort })); }, 1500);
    };
    ['pushState', 'replaceState'].forEach((m) => { const o = history[m]; history[m] = function () { const r = o.apply(this, arguments); setTimeout(onUrl, 0); return r; }; });
    addEventListener('popstate', onUrl);
    document.addEventListener('click', (ev) => { if (ev.target.closest && ev.target.closest('[data-quickbuy], .quick-view, .quickview')) { const card = ev.target.closest(CARD); const p = card ? productOf(card) : {}; if (p.shopifyProductId) track('quick_view_opened', clean({ shopifyProductId: p.shopifyProductId, brand: p.brand, list: listOf(card), position: positionOf(card) })); } }, true);
  }

  /* ─── brands, looks, help, account, wishlist ─── */
  function pageHooks() {
    const t = pageType();
    if (t === 'brand') track('brand_page_viewed', clean({ brandHandle: cfg.page.handle, template: cfg.template }));
    if (t === 'look') track('look_viewed', clean({ lookHandle: cfg.page.handle, pieces: document.querySelectorAll('[data-product-id]').length || undefined }));
    if (t === 'help') track('help_viewed', clean({ topic: cfg.page.handle, from: store.get('omes_last_type') || undefined }));
    if (t === 'cart') cartViewed('page');
    if ((t === 'account' || t === 'page') && /\/(wishlist|favoris)(\/|$)/.test(location.pathname)) track('wishlist_viewed', { count: document.querySelectorAll(CARD).length });
    store.set('omes_last_type', t);
    document.addEventListener('omes:brand-follow-change', (ev) => { const d = ev.detail || {}; if (d.brand) track(d.following === false ? 'brand_unfollowed' : 'brand_followed', { brandHandle: String(d.brand).slice(0, 80) }); });
    document.addEventListener('click', (ev) => {
      const heart = ev.target.closest && ev.target.closest('.addwishlist, .omes-plp-heart, [data-fav]');
      if (!heart) return;
      const id = heart.getAttribute('data-id') || heart.getAttribute('data-fav');
      if (id) track(heart.classList.contains('added') || heart.getAttribute('aria-pressed') === 'true' ? 'wishlist_removed' : 'wishlist_added', { shopifyProductId: String(id) });
    }, true);
    document.addEventListener('submit', (ev) => { const f = ev.target; if (f instanceof HTMLFormElement && /\/contact/.test(f.getAttribute('action') || '')) track('contact_submitted', { subject: (f.querySelector('[name*="subject"], [name*="Sujet"]') || {}).value || 'contact' }); }, true);
    // Help pages fold their answers in <details class="ct-acc">; the older FAQ sections in .aide-faq / .faq.
    document.querySelectorAll(t === 'help' ? 'details' : '.aide-faq details, .faq details, details.ct-acc').forEach((d) => d.addEventListener('toggle', () => { if (d.open) track('faq_opened', { topic: cfg.page.handle || 'aide', question: ((d.querySelector('summary') || {}).textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80) }); }));
  }

  /* ─── « Vu récemment » (TK-30): the products this visitor opened, kept on the phone, drawn on the home and the product page ─── */
  const RECENT_KEY = 'omes_recent';
  const RECENT_MAX = 12;
  function readRecent() { try { const list = JSON.parse(local.get(RECENT_KEY) || '[]'); return Array.isArray(list) ? list.filter((it) => it && it.id && it.url && it.title) : []; } catch (e) { return []; } }
  function rememberProduct(p) {
    if (!consent.personalisation || !p || !p.shopifyProductId || !p.title || !p.url) return;
    const item = clean({ id: String(p.shopifyProductId), title: String(p.title).slice(0, 120), url: String(p.url).split('?')[0], image: p.image || undefined, brand: p.brand || undefined, priceText: p.priceText || undefined, compareText: p.compareText || undefined, at: Date.now() });
    const next = [item].concat(readRecent().filter((it) => it.id !== item.id)).slice(0, RECENT_MAX);
    local.set(RECENT_KEY, JSON.stringify(next));
  }
  function recentRail() {
    const wrap = document.querySelector('[data-omes-recent]');
    if (!wrap) return;
    const grid = wrap.querySelector('.omes-pr-grid');
    const max = Math.max(2, Math.min(12, parseInt(wrap.getAttribute('data-omes-max') || '8', 10) || 8));
    const showBrand = wrap.getAttribute('data-omes-show-brand') !== '0';
    const current = cfg.product && cfg.product.shopifyProductId ? String(cfg.product.shopifyProductId) : null;
    const items = readRecent().filter((it) => it.id !== current).slice(0, max);
    if (!items.length || !grid) return;
    whenFlags(() => {
      const variant = flagVariants['omes-recent-rail'] || 'rail';
      track('feed_shown', { railId: 'recent', count: variant === 'control' ? 0 : items.length, variant });
      if (variant === 'control') return;
      const frag = document.createDocumentFragment();
      items.forEach((it) => {
        const a = document.createElement('a');
        a.className = 'omes-prc'; a.href = it.url; a.setAttribute('data-id', it.id); if (it.brand) a.setAttribute('data-vendor', it.brand);
        const media = document.createElement('div'); media.className = 'omes-prc-media';
        if (it.image) { const img = document.createElement('img'); img.src = it.image; img.alt = it.title; img.loading = 'lazy'; img.decoding = 'async'; media.appendChild(img); }
        if (it.compareText) { const b = document.createElement('span'); b.className = 'omes-prc-badge'; b.textContent = 'Promo'; media.appendChild(b); }
        a.appendChild(media);
        if (showBrand && it.brand) { const br = document.createElement('div'); br.className = 'omes-prc-brand'; br.textContent = it.brand; a.appendChild(br); }
        const name = document.createElement('div'); name.className = 'omes-prc-name'; name.textContent = it.title; a.appendChild(name);
        const price = document.createElement('div'); price.className = 'omes-prc-price';
        if (it.priceText) { const now = document.createElement('span'); now.className = 'now'; now.textContent = it.priceText; price.appendChild(now); }
        if (it.compareText) { const was = document.createElement('span'); was.className = 'was'; was.textContent = it.compareText; price.appendChild(was); }
        a.appendChild(price);
        frag.appendChild(a);
      });
      grid.appendChild(frag);
      wrap.removeAttribute('hidden');
    });
  }

  /* ─── start: consent first ─── */
  let started = false;
  function start() {
    if (started) return;
    started = true;
    openIdentity();
    loadPosthog();
    track('page_viewed', { title: document.title.slice(0, 120) });
    observeSections();
    observeCards(document.body);
    productPage();
    cartHooks();
    if (getCookie('cart')) cartSummary(); // so the first removal of the page can name its line
    searchHooks();
    collectionHooks();
    pageHooks();
    recentRail();
    setTimeout(resolveFlags, 2500); // PostHog absent or slow: the page does not wait for it
  }
  function applyConsent(cp) {
    try {
      consent = { analytics: !!cp.analyticsProcessingAllowed(), personalisation: !!cp.preferencesProcessingAllowed() };
    } catch (e) { consent = { analytics: false, personalisation: false }; }
    if (consent.analytics) start();
  }
  function withPrivacyApi() {
    const S = window.Shopify;
    if (S && S.customerPrivacy) { applyConsent(S.customerPrivacy); return; }
    if (S && typeof S.loadFeatures === 'function') {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => { if (!err && window.Shopify.customerPrivacy) applyConsent(window.Shopify.customerPrivacy); });
      return;
    }
    // Not a Shopify page (a local preview): no API, no consent to read, nothing sent.
  }
  document.addEventListener('visitorConsentCollected', (ev) => {
    const d = ev && ev.detail;
    if (d && typeof d.analyticsAllowed === 'boolean') { consent = { analytics: d.analyticsAllowed, personalisation: !!d.preferencesAllowed }; if (consent.analytics) start(); }
    else if (window.Shopify && window.Shopify.customerPrivacy) applyConsent(window.Shopify.customerPrivacy);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', withPrivacyApi); else withPrivacyApi();
})();
